import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown, Menu, X } from 'lucide-react';

export type Locale = 'sv' | 'en';

type NavigationProps = {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
};

type DropdownKey = 'services' | 'products' | null;

const labels = {
  sv: {
    services: 'Tjänster',
    servicesOverview: 'Tjänsteöversikt',
    serviceItems: [
      ['/apputveckling/', 'Apputveckling', 'Från idé, UX/UI och prototyp till fungerande app.'],
      ['/digitalisering/', 'Digitalisering & interna system', 'Förenkla processer och bygg verksamhetsnära system.'],
      ['/ai-automation/', 'AI & automation', 'AI och automation där det ger mätbar praktisk nytta.'],
    ],
    products: 'Produkter',
    productsOverview: 'Produktöversikt',
    productItems: [
      ['/fampo/', 'FamPo', 'Familjeapp för uppgifter, poäng och belöningar.'],
      ['/#arbete', 'Din Förening', 'Digitalt system för föreningsadministration och kommunikation.'],
    ],
    about: 'Om Keevo',
    contact: 'Kontakt',
    language: 'Språk',
    open: 'Öppna meny',
    close: 'Stäng meny',
  },
  en: {
    services: 'Services',
    servicesOverview: 'Services overview',
    serviceItems: [
      ['/en/app-development/', 'App development', 'From idea, UX/UI and prototype to a working app.'],
      ['/en/digitization/', 'Digitization & internal systems', 'Simplify processes and build operational systems.'],
      ['/en/ai-automation/', 'AI & automation', 'AI and automation where it creates practical value.'],
    ],
    products: 'Products',
    productsOverview: 'Products overview',
    productItems: [
      ['/en/fampo/', 'FamPo', 'Family app for tasks, points and rewards.'],
      ['/en/#arbete', 'Din Förening', 'Digital system for association administration and communication.'],
    ],
    about: 'About Keevo',
    contact: 'Contact',
    language: 'Language',
    open: 'Open menu',
    close: 'Close menu',
  },
} as const;

export function Navigation({ locale, onLocaleChange }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopMenu, setDesktopMenu] = useState<DropdownKey>(null);
  const [activeSection, setActiveSection] = useState('hem');
  const t = labels[locale];

  const pathname = window.location.pathname;
  const homePath = locale === 'en' ? '/en/' : '/';
  const isHomePage = pathname === homePath || (locale === 'en' && pathname === '/en');

  const sectionHref = (id: string) => isHomePage ? `#${id}` : `${homePath}#${id}`;

  const routeActive = useMemo(() => {
    if (
      pathname.startsWith('/apputveckling') ||
      pathname.startsWith('/digitalisering') ||
      pathname.startsWith('/ai-automation') ||
      pathname.startsWith('/en/app-development') ||
      pathname.startsWith('/en/digitization') ||
      pathname.startsWith('/en/ai-automation')
    ) return 'tjanster';

    if (pathname.startsWith('/fampo') || pathname.startsWith('/en/fampo')) return 'arbete';
    if (pathname.startsWith('/om-keevo') || pathname.startsWith('/en/about')) return 'om';
    return null;
  }, [pathname]);

  const active = routeActive ?? activeSection;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isHomePage) return;

    const ids = ['hem', 'tjanster', 'arbete', 'om', 'kontakt'];
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => entry.isIntersecting && setActiveSection(entry.target.id));
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHomePage]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenus = () => {
    setMenuOpen(false);
    setDesktopMenu(null);
  };

  const handleSectionClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    closeMenus();
    if (!isHomePage) return;

    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth' });
  };

  const changeLocale = (nextLocale: Locale) => {
    closeMenus();
    onLocaleChange(nextLocale);
  };

  const desktopLinkClass = (isActive: boolean) =>
    `relative text-[15px] font-medium transition-colors ${isActive ? 'text-[#12141A]' : 'text-[#565A63] hover:text-[#12141A]'}`;

  return (
    <motion.nav
      className="fixed inset-x-0 top-0 z-50"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      aria-label={locale === 'sv' ? 'Huvudnavigation' : 'Main navigation'}
    >
      <div
        className={`absolute inset-0 transition-all duration-500 ${
          scrolled || menuOpen || desktopMenu
            ? 'border-b border-[#E6E4DD]/80 bg-[#F4F3EF]/95 shadow-[0_10px_40px_rgba(31,28,55,0.035)] backdrop-blur-xl'
            : 'bg-[#F4F3EF]/70 backdrop-blur-sm'
        }`}
      />

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-8 lg:px-16">
        <div className="flex h-24 items-center justify-between">
          <motion.a
            href={homePath}
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            aria-label={locale === 'sv' ? 'Keevo startsida' : 'Keevo home'}
            onClick={closeMenus}
          >
            <span
              className="keevo-wordmark text-[26px] tracking-[-0.04em]"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
            >
              <span className="text-[#12141A]">k</span>
              <span className="text-[#5B4BFF]">ee</span>
              <span className="text-[#12141A]">vo</span>
              <span className="text-[#5B4BFF]">.</span>
            </span>
          </motion.a>

          <div className="hidden items-center gap-8 md:flex lg:gap-10">
            <div
              className="relative"
              onMouseEnter={() => setDesktopMenu('services')}
              onMouseLeave={() => setDesktopMenu(null)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDesktopMenu(null);
              }}
            >
              <button
                type="button"
                onClick={() => setDesktopMenu('services')}
                onFocus={() => setDesktopMenu('services')}
                className={`${desktopLinkClass(active === 'tjanster')} inline-flex items-center gap-1.5 py-3`}
                aria-expanded={desktopMenu === 'services'}
                aria-haspopup="menu"
              >
                {t.services}
                <ChevronDown
                  size={15}
                  className={`transition-transform ${desktopMenu === 'services' ? 'rotate-180' : ''}`}
                />
                <span className={`absolute -bottom-0.5 left-0 h-[2px] rounded-full bg-[#5B4BFF] transition-all duration-300 ${active === 'tjanster' ? 'w-full' : 'w-0'}`} />
              </button>

              <AnimatePresence>
                {desktopMenu === 'services' && (
                  <motion.div
                    role="menu"
                    className="absolute left-1/2 top-full w-[430px] -translate-x-1/2 pt-3"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="rounded-[24px] border border-[#E1DEE6] bg-[#FBFAF7] p-3 shadow-[0_24px_70px_rgba(31,28,55,0.14)]">
                      <a
                        href={sectionHref('tjanster')}
                        onClick={(event) => handleSectionClick(event, 'tjanster')}
                        className="mb-1 flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-bold text-[#5B4BFF] transition hover:bg-[#F0EDFF]"
                        role="menuitem"
                      >
                        {t.servicesOverview}
                        <span aria-hidden>→</span>
                      </a>
                      {t.serviceItems.map(([href, title, description]) => (
                        <a
                          key={href}
                          href={href}
                          onClick={closeMenus}
                          className="block rounded-2xl px-4 py-3.5 transition hover:bg-white hover:shadow-[0_8px_24px_rgba(31,28,55,0.06)]"
                          role="menuitem"
                        >
                          <span className="block text-[15px] font-bold text-[#12141A]">{title}</span>
                          <span className="mt-1 block text-[13px] leading-5 text-[#71757E]">{description}</span>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div
              className="relative"
              onMouseEnter={() => setDesktopMenu('products')}
              onMouseLeave={() => setDesktopMenu(null)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDesktopMenu(null);
              }}
            >
              <button
                type="button"
                onClick={() => setDesktopMenu('products')}
                onFocus={() => setDesktopMenu('products')}
                className={`${desktopLinkClass(active === 'arbete')} inline-flex items-center gap-1.5 py-3`}
                aria-expanded={desktopMenu === 'products'}
                aria-haspopup="menu"
              >
                {t.products}
                <ChevronDown
                  size={15}
                  className={`transition-transform ${desktopMenu === 'products' ? 'rotate-180' : ''}`}
                />
                <span className={`absolute -bottom-0.5 left-0 h-[2px] rounded-full bg-[#5B4BFF] transition-all duration-300 ${active === 'arbete' ? 'w-full' : 'w-0'}`} />
              </button>

              <AnimatePresence>
                {desktopMenu === 'products' && (
                  <motion.div
                    role="menu"
                    className="absolute left-1/2 top-full w-[360px] -translate-x-1/2 pt-3"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="rounded-[24px] border border-[#E1DEE6] bg-[#FBFAF7] p-3 shadow-[0_24px_70px_rgba(31,28,55,0.14)]">
                      <a
                        href={sectionHref('arbete')}
                        onClick={(event) => handleSectionClick(event, 'arbete')}
                        className="mb-1 flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-bold text-[#5B4BFF] transition hover:bg-[#F0EDFF]"
                        role="menuitem"
                      >
                        {t.productsOverview}
                        <span aria-hidden>→</span>
                      </a>
                      {t.productItems.map(([href, title, description]) => (
                        <a
                          key={href}
                          href={href}
                          onClick={closeMenus}
                          className="block rounded-2xl px-4 py-3.5 transition hover:bg-white hover:shadow-[0_8px_24px_rgba(31,28,55,0.06)]"
                          role="menuitem"
                        >
                          <span className="block text-[15px] font-bold text-[#12141A]">{title}</span>
                          <span className="mt-1 block text-[13px] leading-5 text-[#71757E]">{description}</span>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href={locale === 'sv' ? '/om-keevo/' : '/en/about/'}
              onClick={closeMenus}
              className={desktopLinkClass(active === 'om')}
            >
              {t.about}
              <span className={`absolute -bottom-2 left-0 h-[2px] rounded-full bg-[#5B4BFF] transition-all duration-300 ${active === 'om' ? 'w-full' : 'w-0'}`} />
            </a>

            <div className="flex items-center gap-1 rounded-full border border-[#DEDCE6] bg-white/65 px-2 py-1.5" aria-label={t.language}>
              <button
                onClick={() => changeLocale('sv')}
                className={`rounded-full px-2 py-1 font-mono text-[11px] font-bold transition-all ${locale === 'sv' ? 'bg-[#EEEAFE] text-[#5B4BFF]' : 'text-[#71757E] hover:text-[#12141A]'}`}
              >
                SV
              </button>
              <button
                onClick={() => changeLocale('en')}
                className={`rounded-full px-2 py-1 font-mono text-[11px] font-bold transition-all ${locale === 'en' ? 'bg-[#EEEAFE] text-[#5B4BFF]' : 'text-[#71757E] hover:text-[#12141A]'}`}
              >
                EN
              </button>
            </div>

            <motion.a
              href={sectionHref('kontakt')}
              onClick={(event) => handleSectionClick(event, 'kontakt')}
              className="relative overflow-hidden rounded-full bg-[#5B4BFF] px-7 py-3 text-[15px] font-semibold text-white shadow-[0_10px_30px_rgba(91,75,255,0.18)]"
              whileHover={{ y: -1, scale: 1.025 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative">{t.contact}</span>
            </motion.a>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <div className="flex items-center gap-0.5 rounded-full border border-[#DEDCE6] bg-white/70 px-1.5 py-1">
              <button onClick={() => changeLocale('sv')} className={`rounded-full px-2 py-1 font-mono text-[10px] font-bold ${locale === 'sv' ? 'bg-[#EEEAFE] text-[#5B4BFF]' : 'text-[#71757E]'}`}>SV</button>
              <button onClick={() => changeLocale('en')} className={`rounded-full px-2 py-1 font-mono text-[10px] font-bold ${locale === 'en' ? 'bg-[#EEEAFE] text-[#5B4BFF]' : 'text-[#71757E]'}`}>EN</button>
            </div>
            <button
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              className="relative z-10 flex h-11 w-11 items-center justify-center text-[#12141A]"
              aria-label={menuOpen ? t.close : t.open}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="relative max-h-[calc(100vh-6rem)] overflow-y-auto border-b border-[#E6E4DD] bg-[#F4F3EF]/98 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mx-auto max-w-[680px] px-7 pb-9 pt-5">
              <div className="border-b border-[#DEDBD4] pb-6">
                <div className="mb-3 text-xs font-bold uppercase tracking-[0.13em] text-[#85858D]">{t.services}</div>
                <a
                  href={sectionHref('tjanster')}
                  onClick={(event) => handleSectionClick(event, 'tjanster')}
                  className="block rounded-xl py-2.5 text-[17px] font-bold text-[#5B4BFF]"
                >
                  {t.servicesOverview}
                </a>
                {t.serviceItems.map(([href, title, description]) => (
                  <a key={href} href={href} onClick={closeMenus} className="block rounded-xl py-3">
                    <span className="block text-[19px] font-semibold text-[#12141A]">{title}</span>
                    <span className="mt-1 block text-[13px] leading-5 text-[#71757E]">{description}</span>
                  </a>
                ))}
              </div>

              <div className="border-b border-[#DEDBD4] py-6">
                <div className="mb-3 text-xs font-bold uppercase tracking-[0.13em] text-[#85858D]">{t.products}</div>
                <a
                  href={sectionHref('arbete')}
                  onClick={(event) => handleSectionClick(event, 'arbete')}
                  className="block rounded-xl py-2.5 text-[17px] font-bold text-[#5B4BFF]"
                >
                  {t.productsOverview}
                </a>
                {t.productItems.map(([href, title, description]) => (
                  <a key={href} href={href} onClick={closeMenus} className="block rounded-xl py-3">
                    <span className="block text-[19px] font-semibold text-[#12141A]">{title}</span>
                    <span className="mt-1 block text-[13px] leading-5 text-[#71757E]">{description}</span>
                  </a>
                ))}
              </div>

              <div className="grid gap-2 py-6">
                <a
                  href={locale === 'sv' ? '/om-keevo/' : '/en/about/'}
                  onClick={closeMenus}
                  className="py-3 text-[24px] font-semibold tracking-[-0.02em] text-[#12141A]"
                >
                  {t.about}
                </a>
              </div>

              <a
                href={sectionHref('kontakt')}
                onClick={(event) => handleSectionClick(event, 'kontakt')}
                className="flex min-h-14 items-center justify-center rounded-full bg-[#5B4BFF] px-8 text-[17px] font-semibold text-white"
              >
                {t.contact}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
